import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import Task from '@src/slide/infrastructure/module/dependent/taskManager/task';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { IObserverParams, ISlideTaskKeyObj } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';

export default class ExecuteBaseManager {
    public static instance;
    public static getInstance(): ExecuteBaseManager {
        if (!this.instance) {
            this.instance = new ExecuteBaseManager();
        }
        return this.instance;
    }

    // presentation数据
    protected presentation: IPresentationModel;
    // 当前页index
    protected currentSlideIndex: number;
    // 存储所有加入任务队列但未被执行的任务taskKey：任务添加时add，任务被完成/被取消时delete
    protected unexecutedTaskKeySet: Set<string> = new Set();

    // 添加监听
    protected addExecuteObserver: (params: IObserverParams) => void;
    // 判断操作是否已执行
    protected checkIsExecuted: (slideIndex: number) => boolean;
    // 任务函数
    protected taskFunc: (params: IObserverParams) => void | undefined;
    // 创建任务key
    protected createTaskKey: (slideKey: string) => string;

    /**
     * 初始化presentation
     * @param presentation
     */
    public initPresentation(presentation: IPresentationModel): void {
        this.presentation = presentation;
    }
    /**
     * 执行对应操作：当前页立即执行，非当前页添加至任务队列
     * @param slideIndex 当前播放页面的index
     */
    public execute(slideIndex: number): void {
        this.currentSlideIndex = slideIndex;
        // 当前页执行操作
        this.executeCurrentSlide();
        // 其他页执行操作
        this.executeSiblingSlide();
    }

    // 取消已经添加到任务队列，但是并未执行的任务
    public cancelUnexecutedTask = (): void | undefined => {
        if (!this.unexecutedTaskKeySet.size) return;
        this.unexecutedTaskKeySet.forEach((taskKey) => {
            if (typeof taskKey !== 'string') return;
            TaskManager.cancelTaskByKey(taskKey);
        });
    };

    /**
     * 向任务队列中添加任务
     * @param slideModel 当前页数据
     * @param slideComponent 当前页canvas渲染树
     */
    protected addNewTask = (slideModel: ISlideModel): void => {
        const taskKey = this.createTaskKey(slideModel.id ?? String(slideModel.id));
        const task = new Task(taskKey, TaskPriority.NORMAL, () => {
            this.taskFunc({ slideModel });
        });
        TaskManager.addTask(task);
        this.unexecutedTaskKeySet.add(taskKey);
        // 任务被正常完成、被取消、被暂停，都会触发then中的回调
        task.promise.then(() => {
            // 任务正常完成，没有被取消，也没有被暂停
            if (task.isFinished && !task.isCanceled && !task.isPaused) {
                this.unexecutedTaskKeySet.delete(taskKey);
            }
            // 任务被取消，注意只是在task中清除了定时器，但是TaskManager中taskMap没有delete taskKey，需要手动清除
            if (task.isCanceled) {
                TaskManager.instance.taskMap.delete(taskKey); // 将被取消的任务，手动从任务队列的map中删除
                this.unexecutedTaskKeySet.delete(taskKey); // 将被取消的任务，从已添加到任务队列但未转换的map中删除
            }
            // 任务被暂停，说明任务没有完成，该任务无需从任务队列的map中删除，也无需从unConvertedTimingTaskSet中删除
        });
    };

    /**
     * 处理已经添加到任务队列，但并未执行的任务。默认处理逻辑为：降低其优先级。
     * 如有其他处理，可在子类中override此方法
     * @param needExecuteSlideIndexList 当前需要进行提前处理的slideList
     */
    protected handleUnexecutedTask = (needExecuteSlideIndexList: ISlideTaskKeyObj[]): void => {
        this.unexecutedTaskKeySet.forEach((unexecutedTaskKey) => {
            needExecuteSlideIndexList.forEach(({ taskKey }) => {
                if (unexecutedTaskKey === taskKey) return;
                const unexecutedTask = TaskManager.getTaskWithKey(unexecutedTaskKey);
                if (unexecutedTask) {
                    unexecutedTask.priority = unexecutedTask.priority - 1;
                }
            });
        });
    };

    // 其他禁止执行当前页转换的逻辑，默认false，转换动画数据室需要判断是否有动画数据
    protected needStopExecuteCurrentSlide = (slideModel: ISlideModel): boolean => false;

    // 当前页执行相应操作
    private executeCurrentSlide = (): void => {
        const currentSlideModel = this.presentation.children[this.currentSlideIndex];
        if (!currentSlideModel) return;
        if (this.needStopExecuteCurrentSlide(currentSlideModel)) return;
        const isExecuted = this.checkIsExecuted(this.currentSlideIndex);
        // 切换到当前页，如果任务队列中的任务正在执行中，或者 如果已经执行完了，则不必进行当前页转换
        if (isExecuted  || this.checkIsExecuting(currentSlideModel.key)) return;
        // 切换到当前页，如果任务队列中的任务还没有被执行，则取消该任务
        TaskManager.cancelTaskByKey(this.createTaskKey(currentSlideModel.key));
        this.taskFunc({ slideModel: currentSlideModel });
    };

    // 非当前页执行相应操作
    private executeSiblingSlide = (): void => {
        const needExecuteSlideIndexList = this.getNeedAdvanceExecuteSlideIndexList();
        if (!needExecuteSlideIndexList.length) return;
        // 1、处理已添加至任务队列，但并未执行的任务
        this.handleUnexecutedTask(needExecuteSlideIndexList);
        // 2、需要提前处理的页面，添加至任务队列中
        needExecuteSlideIndexList.forEach(({ slideIndex, taskKey }) => {
            const isExecuted = this.checkIsExecuted(slideIndex);
            if (isExecuted) return;
            const slideModel = this.presentation.children[slideIndex];
            const isTaskInManager = this.checkIfInTaskManager(taskKey);
            if (isTaskInManager) return;
            this.addNewTask(slideModel);
        });
    };

    //  获取需要进行提前处理的页面slideIndexList
    private getNeedAdvanceExecuteSlideIndexList = (): ISlideTaskKeyObj[] => {
        const slideCount = this.presentation?.slideCount;
        // slideCount 有可能等于 -1
        if (!slideCount || slideCount < 1 || slideCount === 1) return [];

        const needExecuteSlideIndexList: { slideIndex: number; taskKey: string }[] = [];
        [this.currentSlideIndex - 1, this.currentSlideIndex + 1].forEach((slideIndex) => {
            const slideModel = this.presentation.children[slideIndex];
            if (!slideModel) return;
            needExecuteSlideIndexList.push({ slideIndex, taskKey: this.createTaskKey(slideModel.key) });
        });

        return needExecuteSlideIndexList;
    };

    // 判断是否已添加到任务队列中
    private checkIfInTaskManager = (key: string): boolean => !!TaskManager.getTaskWithKey(key);

    // 检查任务是否正在执行
    private checkIsExecuting = (slideModelKey: string): boolean => {
        const taskKey = this.createTaskKey(slideModelKey);
        const task = TaskManager.getTaskWithKey(taskKey);
        return !!task?.isExecuting;
    };
}
